import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Form, Button, Spinner, Alert, Badge } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { TranslatorContext } from "../../../context/Translator";

export default function WebOrdersListPage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [marketplaces, setMarketplaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalOrders, setTotalOrders] = useState(0);

    const [search, setSearch] = useState("");
    const [marketplaceId, setMarketplaceId] = useState("");
    const [paymentStatus, setPaymentStatus] = useState("");
    const [deliveryStatus, setDeliveryStatus] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const tableHeaders = [
        { name: "ORDER #", width: "130px" },
        { name: "MARKETPLACE", width: "140px" },
        { name: "BUYER NAME", width: "140px" },
        { name: "GRAND TOTAL", width: "110px" },
        { name: "PAYMENT STATUS", width: "120px" },
        { name: "DELIVERY STATUS", width: "120px" },
        { name: "ITEMS", width: "70px" },
        { name: "ORDERED AT", width: "120px" },
        { name: "ACTION", width: "90px" },
    ];

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/admin/web-orders/initial-data`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
                );

                // API Key matching backend initialData()
                const list = response.data?.market_places || response.data?.marketplaces || [];
                setMarketplaces(list);
            } catch (err) {
                console.error("Failed to load marketplaces for filter", err);
            }
        };

        fetchInitialData();
    }, []);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {};
            if (search) params.search = search;
            if (marketplaceId) params.marketplace_id = marketplaceId;
            if (paymentStatus) params.payment_status = paymentStatus;
            if (deliveryStatus) params.delivery_status = deliveryStatus;
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/web-orders`,
                { params, headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
            );

            if (response.data?.success) {
                setOrders(response.data.data || []);
                setTotalOrders(response.data.total_orders || response.data.data?.length || 0);
            } else {
                setOrders([]);
                setTotalOrders(0);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch website orders. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [search, marketplaceId, paymentStatus, deliveryStatus, fromDate, toDate]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchOrders();
    };

    const handleResetFilters = () => {
        setSearch("");
        setMarketplaceId("");
        setPaymentStatus("");
        setDeliveryStatus("");
        setFromDate("");
        setToDate("");
    };

    const getMarketplaceName = (item) => {
        if (!item) return "";
        if (typeof item === "string") return item;
        return item.name || item.title || item.marketplace_name || item.label || "";
    };

    const getPaymentBadge = (status) => {
        switch (status) {
            case "Paid": return <Badge bg="success">Paid</Badge>;
            case "Pending": return <Badge bg="warning" text="dark">Pending</Badge>;
            case "Refunded": return <Badge bg="info">Refunded</Badge>;
            case "Cancelled": return <Badge bg="danger">Cancelled</Badge>;
            default: return <Badge bg="secondary">{status || "N/A"}</Badge>;
        }
    };

    const getDeliveryBadge = (status) => {
        switch (status) {
            case "Delivered": return <Badge bg="success">Delivered</Badge>;
            case "Shipped": return <Badge bg="primary">Shipped</Badge>;
            case "Pending": return <Badge bg="warning" text="dark">Pending</Badge>;
            case "Returned": return <Badge bg="danger">Returned</Badge>;
            default: return <Badge bg="secondary">{status || "N/A"}</Badge>;
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Website Orders ({totalOrders})</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t("home") || "Home"}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">Web Orders</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card mb-4 p-3 p-md-4">
                        <Form onSubmit={handleFilterSubmit}>
                            <Row className="g-3 align-items-end">
                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>SEARCH</Form.Label>
                                        <Form.Control type="text" placeholder="Order # or Buyer..." value={search} onChange={(e) => setSearch(e.target.value)} />
                                    </Form.Group>
                                </Col>

                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>MARKETPLACE</Form.Label>
                                        <Form.Select value={marketplaceId} onChange={(e) => setMarketplaceId(e.target.value)}>
                                            <option value="">All Marketplaces</option>
                                            {marketplaces.map((m) => (
                                                <option key={m.id} value={m.id}>
                                                    {getMarketplaceName(m) || `Marketplace #${m.id}`}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>PAYMENT STATUS</Form.Label>
                                        <Form.Select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                                            <option value="">All Payment Statuses</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Paid">Paid</option>
                                            <option value="Refunded">Refunded</option>
                                            <option value="Cancelled">Cancelled</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>DELIVERY STATUS</Form.Label>
                                        <Form.Select value={deliveryStatus} onChange={(e) => setDeliveryStatus(e.target.value)}>
                                            <option value="">All Delivery Statuses</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Shipped">Shipped</option>
                                            <option value="Delivered">Delivered</option>
                                            <option value="Returned">Returned</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>FROM DATE</Form.Label>
                                        <Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                                    </Form.Group>
                                </Col>

                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>TO DATE</Form.Label>
                                        <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                                    </Form.Group>
                                </Col>

                                <Col xl={12} className="d-flex justify-content-end gap-2 mt-3">
                                    <Button type="submit" variant="primary" className="text-uppercase fw-semibold px-4" style={{ backgroundColor: "#2b77c0", borderColor: "#2b77c0", height: "38px" }}>
                                        FILTER
                                    </Button>
                                    <Button type="button" variant="light" className="text-uppercase fw-semibold border px-4" onClick={handleResetFilters} style={{ height: "38px" }}>
                                        RESET
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card p-0" style={{ overflow: "hidden" }}>
                        {error && (
                            <div className="p-3">
                                <Alert variant="danger" className="m-0">{error}</Alert>
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">Loading website orders...</p>
                            </div>
                        ) : (
                            <div className="w-100" style={{ overflowX: "auto" }}>
                                <table className="table align-middle text-center table-hover mb-0" style={{ width: "100%", minWidth: "1050px", tableLayout: "fixed" }}>
                                    <thead>
                                        <tr style={{ backgroundColor: "#2b77c0", color: "#ffffff" }}>
                                            {tableHeaders.map((header, index) => (
                                                <th key={index} style={{ width: header.width, padding: "12px 8px", fontSize: "12px", fontWeight: "700", textTransform: "uppercase", color: "#ffffff", border: "none" }}>
                                                    {header.name}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orders.length > 0 ? (
                                            orders.map((item) => (
                                                <tr key={item.id} style={{ borderBottom: "1px solid #e9ecef" }}>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <span className="fw-bold text-primary text-truncate d-block">{item.order_number || `#${item.id}`}</span>
                                                    </td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <Badge
                                                            bg="warning"
                                                            text="dark"
                                                            className="px-2 py-1 text-uppercase fw-bold text-truncate d-inline-block"
                                                            style={{ maxWidth: "120px", fontSize: "11px", letterSpacing: "0.5px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
                                                        >
                                                            🏷️ {getMarketplaceName(item.marketplace) || item.marketplace_name || "Website"}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-start text-truncate" style={{ padding: "12px 8px" }}>{item.buyer?.name || item.buyer_name || "N/A"}</td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <span className="fw-bold text-dark">{item.grand_total ? Number(item.grand_total).toLocaleString() : "0.00"}</span>
                                                    </td>
                                                    <td style={{ padding: "12px 8px" }}>{getPaymentBadge(item.payment?.status || item.payment_status)}</td>
                                                    <td style={{ padding: "12px 8px" }}>{getDeliveryBadge(item.delivery?.status || item.delivery_status)}</td>
                                                    <td style={{ padding: "12px 8px" }}>{item.products ? item.products.length : (item.items_count || 0)}</td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        {item.dates?.ordered_at ? new Date(item.dates.ordered_at).toLocaleDateString() : (item.ordered_at ? new Date(item.ordered_at).toLocaleDateString() : "N/A")}
                                                    </td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <Button variant="outline-primary" size="sm" className="px-2 py-1 text-nowrap" onClick={() => navigate(`/admin/web-orders/${item.id}/edit`)} style={{ fontSize: "12px" }}>
                                                            EDIT
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={tableHeaders.length} className="text-center py-4 text-muted">No website orders found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}